import { z } from 'zod'
import { selectedElectricityProductionMethodSchema } from './ElectricityProductionMethodsModel'
import { EmissionReport } from './EmissionReportModel'
import { createRequiredLatinCharacterSchema } from '@/common/utils/latinCharacterValidation'

export enum StoredCO2Handling {
  None = 'NONE_STORED_CO2',
  Capture = 'CAPTURE',
  StorageSite = 'STORAGE_SITE',
  InstallationOrTransportNetwork = 'INSTALLATION_OR_TRANSPORT_NETWORK',
  Production = 'PRODUCTION'
}

export enum HeatFlowSystems {
  None = 'NONE_HEAT_FLOW',
  NoCogeneration = 'INTERNAL_NO_COGENERATION',
  Cogeneration = 'INTERNAL_COGENERATION',
  External = 'EXTERNAL'
}

export const productionRouteAssetSchema = z.object({
  id: z.number(),
  assetDefinitionId: z.string().nullable(),
  quantity: z.number().default(1),
  imageUrl: z.string().optional().nullable(),
  customId: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  customName: z.string().optional().nullable(),
  isCustom: z.boolean().default(false),
  emissionPoint: z.string(),
  usesFuel: z.boolean().default(false)
})

export type ProductionRouteAsset = z.infer<typeof productionRouteAssetSchema>

export const routeProductionMethodSchema = z.object({
  code: z.string(),
  cnCodes: z.array(z.string())
})

export type RouteProductionMethod = z.infer<typeof routeProductionMethodSchema>

const definitionFields = {
  aggregatedGood: z.string(),
  productionMethods: z.array(routeProductionMethodSchema)
}

export const definitionSchema = z.object(definitionFields)

// State fields with nullable values for initial form state
const producionRouteFields = {
  ...definitionFields,
  installationId: z.string(),
  name: createRequiredLatinCharacterSchema('Production route name must use Latin characters'),
  assets: z.array(productionRouteAssetSchema),
  heatFlowSystem: z.enum(HeatFlowSystems).nullable(),
  heatFlowSodaAshPurification: z.boolean().nullable(),
  heatFlowLimestonePurification: z.boolean().nullable(),
  isUsingWasteGas: z.boolean().nullable(),
  electricityProductionMethods: z.array(selectedElectricityProductionMethodSchema),
  storedCO2Handling: z.enum(StoredCO2Handling).nullable()
}

// Payload fields without nullable (for API calls)
const productionRouteCreatePayloadFields = {
  ...definitionFields,
  installationId: z.string(),
  name: createRequiredLatinCharacterSchema('Production route name must use Latin characters'),
  assets: z.array(productionRouteAssetSchema),
  heatFlowSystem: z.enum(HeatFlowSystems),
  heatFlowSodaAshPurification: z.boolean(),
  heatFlowLimestonePurification: z.boolean(),
  isUsingWasteGas: z.boolean(),
  electricityProductionMethods: z.array(selectedElectricityProductionMethodSchema),
  storedCO2Handling: z.enum(StoredCO2Handling)
}

const productionRouteUpdatePayloadFields = {
  name: createRequiredLatinCharacterSchema('Production route name must use Latin characters')
}

export const productionRouteCreatePayloadSchema = z.object(productionRouteCreatePayloadFields)

export const productionRouteUpdatePayloadSchema = z.object(productionRouteUpdatePayloadFields)

const productionRouteCreateStateSchema = z.object(producionRouteFields)

export type ProductionRouteCreatePayload = z.infer<typeof productionRouteCreatePayloadSchema>

export type ProductionRouteCreationState = z.infer<typeof productionRouteCreateStateSchema>

export type ProductionRouteUpdatePayload = z.infer<typeof productionRouteUpdatePayloadSchema>

export interface ProductionRoute extends ProductionRouteCreatePayload {
  id: string
  createdAt: string
  updatedAt?: string
  deletedAt?: string
}
export interface ProductionRouteWithReports extends ProductionRouteCreatePayload {
  id: string
  createdAt: string
  updatedAt?: string
  deletedAt?: string
  reports?: EmissionReport[]
}
